import { motion, MotionConfig } from "framer-motion";
import { Check, ChevronRight, BookOpen, Star, Crown } from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import { cn } from "../../../components/ui/utils";
import { useInViewOnce } from "../../../hooks/useInViewOnce";

type PlanTier = "entry" | "recommended" | "enterprise";

const PLANS: {
  id: string;
  name: string;
  subtitle: string;
  price: string;
  priceAnchor: string;
  description: string;
  badge: string | null;
  ribbon: string | null;
  features: string[];
  cta: string;
  tier: PlanTier;
  icon: typeof BookOpen;
}[] = [
  {
    id: "campus",
    name: "Neuroment Campus",
    subtitle: "Comece com uma unidade",
    price: "Sob consulta",
    priceAnchor: "Entrada acessível para escolas em digitalização",
    description:
      "O essencial para publicar conteúdos, acompanhar turmas e dar visibilidade à família — sem excesso de complexidade no primeiro ano.",
    badge: null,
    ribbon: null,
    tier: "entry",
    icon: BookOpen,
    features: [
      "Personalização por perfil para alunos da unidade contratada",
      "Turmas, salas e calendário acadêmico (conforme roadmap de implantação)",
      "Camada de notas e atividades separada da gamificação",
      "Suporte em canal institucional em horário comercial",
      "Implementação guiada em prazo combinado",
    ],
    cta: "Solicitar proposta Campus",
  },
  {
    id: "educacao",
    name: "Neuroment Educação",
    subtitle: "O equilíbrio que a maioria escolhe",
    price: "Sob consulta",
    priceAnchor: "Melhor custo-benefício para escola em plena operação",
    description:
      "Pacote completo para direção pedagógica que quer dados, coordenação alinhada e família dentro do fluxo.",
    badge: "Mais escolhido",
    ribbon: "Preferido por direções pedagógicas",
    tier: "recommended",
    icon: Star,
    features: [
      "Tudo do Campus, com prioridade na fila de novas funcionalidades",
      "Painéis para coordenação: turma, disciplina, professor e risco",
      "Importação em lote de alunos e relatórios exportáveis (roadmap)",
      "Onboarding estendido + suporte prioritário",
      "SLA de resposta ampliado e revisões de uso semestrais inclusas",
    ],
    cta: "Quero o Educação — recomendado",
  },
  {
    id: "rede",
    name: "Neuroment Rede",
    subtitle: "Mantenedora e multi-unidade",
    price: "Sob consulta",
    priceAnchor: "Governança e escala para grupos educacionais",
    description:
      "Para redes que precisam padronizar experiência, integrar sistemas e ter um interlocutor dedicado em cada fase do crescimento.",
    badge: null,
    ribbon: null,
    tier: "enterprise",
    icon: Crown,
    features: [
      "Tudo do Educação aplicado a várias unidades com visão consolidada",
      "API, integrações e ambientes alinhados ao seu SIS/LMS (sob escopo)",
      "Comitê de produto e roadmap compartilhado",
      "Gerente de conta e canal direto com o time Neuroment",
      "Contrato, SLA premium e opções de customização",
    ],
    cta: "Falar com especialista Rede",
  },
];

interface PlansProps {
  onPlanSelect: (planId: string) => void;
  onSignup: () => void;
}

export function PlansSection({ onPlanSelect, onSignup }: PlansProps) {
  const { ref, isVisible } = useInViewOnce();

  return (
    <MotionConfig reducedMotion="user">
      <section
        id="planos"
        ref={ref}
        className="scroll-mt-20 border-b border-white/[0.06] py-16 sm:scroll-mt-24 sm:py-20 lg:py-24"
      >
        <div className="container mx-auto max-w-7xl px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: [0, 0, 0.2, 1] }}
            className="mx-auto max-w-3xl text-center"
          >
            <Badge className="mb-4 border-emerald-500/30 bg-emerald-500/10 text-emerald-200">
              Planos institucionais
            </Badge>
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl md:text-4xl">
              Três formas de levar o NeuroMent para{" "}
              <span className="bg-gradient-to-r from-emerald-300 to-teal-300 bg-clip-text text-transparent">
                sua escola
              </span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-400 sm:text-lg">
              Valores sob consulta — cada proposta considera porte, unidades e prazo
              de implantação. Na dúvida, a maioria das direções pede o{" "}
              <strong className="font-medium text-slate-200">plano do meio</strong>.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.65, delay: 0.15, ease: [0, 0, 0.2, 1] }}
            className="relative mx-auto mt-10 max-w-6xl sm:mt-14"
          >
            <p className="mb-6 text-center text-sm text-teal-200/90 sm:mb-8 sm:text-base">
              <Star className="mb-1 mr-1 inline h-4 w-4 text-amber-400" aria-hidden />
              Efeito "compromisso": o Campus ancora o preço baixo, o Rede ancora o
              topo — e o <span className="font-medium text-white">Educação</span> fica
              no ponto ótimo para a maioria das escolas.
            </p>

            <div className="grid auto-rows-fr gap-6 lg:grid-cols-3 lg:items-stretch lg:gap-5 xl:gap-6">
              {PLANS.map((plan) => {
                const isRec = plan.tier === "recommended";
                const mobileOrder =
                  plan.tier === "recommended"
                    ? "order-first"
                    : plan.tier === "entry"
                      ? "order-2"
                      : "order-3";

                return (
                  <Card
                    key={plan.id}
                    className={cn(
                      "relative flex flex-col overflow-hidden border backdrop-blur-md transition-[border-color,box-shadow,transform] duration-300",
                      mobileOrder,
                      "lg:order-none",
                      isRec
                        ? "z-[1] border-teal-400/50 bg-gradient-to-b from-teal-950/90 to-slate-900/95 shadow-2xl shadow-teal-900/35 ring-2 ring-teal-400/25 lg:scale-[1.04] lg:shadow-teal-900/50"
                        : "border-white/10 bg-white/[0.04] hover:border-white/20"
                    )}
                  >
                    {isRec && plan.ribbon && (
                      <div className="bg-gradient-to-r from-amber-500/90 via-teal-500 to-cyan-600 py-2.5 text-center text-xs font-semibold uppercase tracking-wide text-white sm:text-sm">
                        {plan.ribbon}
                      </div>
                    )}
                    {plan.badge && (
                      <div
                        className={cn(
                          "absolute z-10",
                          isRec
                            ? "right-3 top-12 sm:right-4 sm:top-14"
                            : "right-3 top-3 sm:right-4 sm:top-4"
                        )}
                      >
                        <Badge className="border-amber-400/50 bg-amber-500/25 text-amber-100">
                          {plan.badge}
                        </Badge>
                      </div>
                    )}

                    <div
                      className={cn(
                        "flex flex-1 flex-col p-5 sm:p-6 lg:p-7",
                        isRec && !plan.ribbon && "pt-6"
                      )}
                    >
                      <div className="mb-3 flex items-start gap-3">
                        <div
                          className={cn(
                            "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl sm:h-12 sm:w-12",
                            isRec
                              ? "bg-gradient-to-br from-teal-400 to-cyan-500 shadow-lg"
                              : "bg-white/10"
                          )}
                        >
                          <plan.icon className="h-5 w-5 text-white sm:h-6 sm:w-6" aria-hidden />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-display text-lg font-semibold leading-tight text-white sm:text-xl">
                            {plan.name}
                          </h3>
                          <p className="mt-0.5 text-xs text-teal-200/80 sm:text-sm">
                            {plan.subtitle}
                          </p>
                        </div>
                      </div>

                      <p className="mb-4 text-sm leading-relaxed text-slate-400">
                        {plan.description}
                      </p>

                      <div className="mb-1">
                        <span className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                          {plan.price}
                        </span>
                      </div>
                      <p className="mb-5 text-xs leading-snug text-slate-500 sm:mb-6 sm:text-sm">
                        {plan.priceAnchor}
                      </p>

                      <Button
                        onClick={() => onPlanSelect(plan.id)}
                        className={cn(
                          "mb-6 h-auto min-h-11 w-full whitespace-normal text-balance text-sm font-semibold leading-snug sm:h-12 sm:min-h-0 sm:text-base",
                          isRec
                            ? "landing-cta-glow border-0 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-400 hover:to-cyan-500"
                            : "border border-white/20 bg-white/10 text-white hover:border-white/35 hover:bg-white/15"
                        )}
                      >
                        {plan.cta}
                        <ChevronRight className="ml-2 h-4 w-4" aria-hidden />
                      </Button>

                      <ul className="mt-auto space-y-2.5 border-t border-white/10 pt-5 sm:space-y-3">
                        {plan.features.map((f) => (
                          <li key={f} className="flex gap-2.5 text-xs sm:gap-3 sm:text-sm">
                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-500/20">
                              <Check className="h-3 w-3 text-teal-300" aria-hidden />
                            </span>
                            <span className="text-slate-200">{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Card>
                );
              })}
            </div>

            <p className="mx-auto mt-8 max-w-2xl text-center text-xs leading-relaxed text-slate-500 sm:mt-10 sm:text-sm">
              Contrato e nota fiscal sob medida · LGPD e termos alinhados ao uso
              escolar ·{" "}
              <button
                type="button"
                onClick={onSignup}
                className="text-teal-400 underline-offset-2 hover:text-teal-300 hover:underline"
              >
                Cadastro piloto
              </button>{" "}
              disponível para homologação técnica, sem substituir a contratação
              institucional.
            </p>
          </motion.div>
        </div>
      </section>
    </MotionConfig>
  );
}
